// src/controllers/useSelectableCardController.js
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL, BASE_IMAGE_URL } from "../utils/api";
import Swal from "sweetalert2";
import { pushAddToCart } from "../utils/gtmAddToCart"; // adjust path if needed

export default function useSelectableCardController(item, setCurrentView, onWishlistChange) {
  const [hovered, setHovered] = useState(false);
  if (!item) {
    return {
      // safe defaults for early return in the view
      isPart: false,
      isCategory: false,
      isBrand: false,
      isModel: false,
      imageSrc: "",
      id: undefined,
      label: "",
      price: undefined,
      market_price: undefined,
      hovered,
      setHovered,
    };
  }

  // --- Detect type (unchanged logic)
  const isPart = !!item.image_url || !!(item.images && item.images[0].image_url);
  const isCategory = !!item.icon;
  const isBrand = !!item.image;
  const isModel = !!item.logo;

  // --- Resolve image source (unchanged logic)
  const resolveSrc = () => {

    if (isPart) return item.image_url ? `${BASE_IMAGE_URL}${item.image_url}` : `${BASE_IMAGE_URL}${item.images[0].image_url}`;
    if (isCategory) {
      if (typeof item.icon === "string" && item.icon.startsWith("http")) return item.icon;
      return `${BASE_IMAGE_URL}${item.icon}`;
    }
    if (isBrand) return `${BASE_IMAGE_URL}${item.image}`;
    if (isModel) return `${BASE_IMAGE_URL}${item.logo}`;
    return "";
  };
  const imageSrc = resolveSrc();

  const id = item.id;
  const label = item.name || item.year || "";
  const price = item.price ?? item.selling_price ?? item.total_price;
  const market_price = item.market_price;

  // ------------- Share box (unchanged behavior) -------------
  const shareRef = useRef(null);
  const [showShareBox, setShowShareBox] = useState(false);
  const partUrl = `${window.location.origin}/part/${item?.id ?? ""}`;

  useEffect(() => {
    function handle(e) {
      if (!shareRef.current) return;
      if (!shareRef.current.contains(e.target)) setShowShareBox(false);
    }
    if (showShareBox) {
      document.addEventListener("mousedown", handle);
      document.addEventListener("touchstart", handle);
    }
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("touchstart", handle);
    };
  }, [showShareBox]);

  // ------------- Wishlist (unchanged behavior) -------------
  const [inWishlist, setInWishlist] = useState(false);
  const [wishLoading, setWishLoading] = useState(false);

  // ===== 1) Initialize: fetch wishlist if authed =====
  // ==== helpers: localStorage wishlist ids =====================================
  const WISHLIST_KEY = "wishlist"; // stores an array of numeric IDs, e.g. [12, 55, 99]

  const safeParse = (str) => {
    try { return JSON.parse(str); } catch { return null; }
  };

  const readWishlistIds = () => {
    const raw = localStorage.getItem(WISHLIST_KEY);
    const parsed = safeParse(raw);
    return Array.isArray(parsed) ? parsed.map((n) => Number(n)).filter(Number.isFinite) : [];
  };

  const writeWishlistIds = (ids) => {
    // ensure unique, numeric, sorted (optional but nice)
    const unique = Array.from(new Set(ids.map((n) => Number(n)).filter(Number.isFinite)));
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(unique));
    return unique;
  };

  const addIdToLocalWishlist = (id) => {
    const ids = readWishlistIds();
    if (!ids.includes(Number(id))) {
      ids.push(Number(id));
      writeWishlistIds(ids);
    }
  };

  const removeIdFromLocalWishlist = (id) => {
    const ids = readWishlistIds().filter((n) => n !== Number(id));
    writeWishlistIds(ids);
  };

  const isInLocalWishlist = (id) => {
    const ids = readWishlistIds();
    return ids.includes(Number(id));
  };

  // ==== 1) Init: decide inWishlist from localStorage (no network) ==============
  useEffect(() => {
    let cancelled = false;

    // Set from localStorage immediately
    if (id != null) {
      const localFound = isInLocalWishlist(id);
      if (!cancelled) setInWishlist(localFound);
    } else {
      if (!cancelled) setInWishlist(false);
    }

    // Optional: sync across tabs/windows
    const onStorage = (e) => {
      if (e.key === WISHLIST_KEY && id != null && !cancelled) {
        setInWishlist(isInLocalWishlist(id));
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
    };
  }, [id]);

  // ==== 2) Toggle wishlist (add/remove) with alerts + localStorage update ======
  const handleToggleWishlist = async (e) => {
    e?.stopPropagation?.();
    if (wishLoading || !id) return;

    const token = localStorage.getItem("token");
    if (!token) {
      // Guard: normally handled by onWishlistClick
      return;
    }

    setWishLoading(true);
    try {
      if (inWishlist) {
        // REMOVE from backend
        await axios.delete(`${API_BASE_URL}/wishlist/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Update local state + localStorage
        setInWishlist(false);
        removeIdFromLocalWishlist(id);

        Swal.fire({
          icon: "success",
          title: "تمت الإزالة",
          text: "تمت إزالة القطعة من المفضلة ✅",
          confirmButtonText: "حسناً",
          confirmButtonColor: "#CAEA3E",
          iconColor: "#CAEA3E",
          customClass: {
            popup: "swal-dark-popup",
            cancelButton: "swal-cancel-btn",
          },
        });

        onWishlistChange?.({ id, nowInWishlist: false });
      } else {
        // ADD to backend
        await axios.post(
          `${API_BASE_URL}/wishlist`,
          { part_id: id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Update local state + localStorage
        setInWishlist(true);
        addIdToLocalWishlist(id);

        Swal.fire({
          icon: "success",
          title: "تمت الإضافة",
          text: "تمت إضافة القطعة إلى المفضلة ✅",
          confirmButtonText: "حسناً",
          confirmButtonColor: "#CAEA3E",
          iconColor: "#CAEA3E",
          customClass: {
            popup: "swal-dark-popup",
            cancelButton: "swal-cancel-btn",
          },
        });

        onWishlistChange?.({ id, nowInWishlist: true });
      }
    } catch (err) {
      console.error("Wishlist toggle failed:", err);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: "حدث خطأ أثناء تحديث المفضلة ❌",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        customClass: {
          popup: "swal-dark-popup",
          cancelButton: "swal-cancel-btn",
        },
      });
    } finally {
      setWishLoading(false);
    }
  };

  // ==== 3) Public click that handles NOT AUTHED via Swal =======================
  const onWishlistClick = async (e) => {
    e.stopPropagation();
    if (wishLoading) return;

    const token = localStorage.getItem("token");
    if (!token) {
      const res = await Swal.fire({
        icon: "info",
        title: "يرجى تسجيل الدخول",
        text: "سجل دخول عشان تقدر تضيف قطعتك للمفضلة",
        showCancelButton: true,
        confirmButtonText: "تسجيل الدخول",
        cancelButtonText: "إلغاء",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        customClass: {
          popup: "swal-dark-popup",
          cancelButton: "swal-cancel-btn",
        },
      });

      if (res.isConfirmed) {
        setCurrentView?.("login");
        // or: window.location.assign('/login');
      }
      return;
    }

    // Authed -> do the toggle (add/remove)
    await handleToggleWishlist(e);
  };


  // ------------- Cart (auth) -------------
  const [cartItemId, setCartItemId] = useState(null); // null = not in cart
  const [checkingCart, setCheckingCart] = useState(false);

  // check if part already in pending cart (auth)
  useEffect(() => {
    if (!isPart) return;
    const token = localStorage.getItem("token");
    if (!token) {
      setCartItemId(null);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        setCheckingCart(true);
        const res = await axios.get(`${API_BASE_URL}/carts/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const arr = Array.isArray(res.data) ? res.data : [];
        const cart = arr[0];
        if (!cart) {
          if (!cancelled) setCartItemId(null);
          return;
        }
        const found = (cart.items || []).find((ci) => Number(ci.part_id) === Number(item.id));
        if (!cancelled) setCartItemId(found ? found.id : null);
      } catch (err) {
        console.error("Check part in cart failed:", err);
        if (!cancelled) setCartItemId(null);
      } finally {
        if (!cancelled) setCheckingCart(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isPart, item?.id]);

  const handleAddAuth = async () => {
    const token = localStorage.getItem("token");
    try {
      const payload = {
        items: [
          {
            part_id: item.id,
            price: item.price ?? 0,
          },
        ],
      };
      const res = await axios.post(`${API_BASE_URL}/add/carts`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const added =
        res.data?.cart?.items?.find((ci) => Number(ci.part_id) === Number(item.id)) ?? null;

      if (added?.id) {
        setCartItemId(added.id);

        // ✅ Track only on confirmed success
        pushAddToCart({
          item_id: item.id,                          // part id
          item_name: item.name ?? item.label ?? "",  // part name
          price: item.price ?? 0,                    // part price
          quantity: 1                                // one item per click
        });

        await Swal.fire({
          icon: "success",
          title: "تمت الإضافة",
          text: "✅ تمت إضافة القطعة إلى السلة بنجاح!",
          confirmButtonColor: "#CAEA3E",
          iconColor: "#CAEA3E",
          customClass: {
            popup: "swal-dark-popup",
            cancelButton: "swal-cancel-btn",
          },
        });
      } else {
        // fallback: re-check pending cart
        try {
          const again = await axios.get(`${API_BASE_URL}/carts/pending`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const arr = Array.isArray(again.data) ? again.data : [];
          const cart = arr[0];
          const found = cart?.items?.find((ci) => Number(ci.part_id) === Number(item.id));
          setCartItemId(found ? found.id : null);

          // ✅ Track only if we positively located the item in the pending cart
          if (found?.id) {
            pushAddToCart({
              item_id: item.id,
              item_name: item.name ?? item.label ?? "",
              price: item.price ?? 0,
              quantity: 1
            });
          }
        } catch { }

        await Swal.fire({
          icon: "warning",
          title: "تنبيه",
          text: "⚠️ تمت المعالجة، لكن لم نستطع تحديد العنصر المُضاف من الاستجابة.",
          confirmButtonColor: "#CAEA3E",
          iconColor: "#CAEA3E",
          customClass: {
            popup: "swal-dark-popup",
            cancelButton: "swal-cancel-btn",
          },
        });
      }
    } catch (err) {
      console.error(err);

      await Swal.fire({
        icon: "error",
        title: "خطأ",
        text: "⚠️ لم نستطع إضافة القطعة إلى السلة.",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        customClass: {
          popup: "swal-dark-popup",
          cancelButton: "swal-cancel-btn",
        },
      });
    }
  };

  const handleRemove = async (e) => {
    e?.stopPropagation?.();;
    const token = localStorage.getItem("token");
    if (!token) {
      alert("يجب أن تسجل دخول أولاً");
      return;
    }
    if (!cartItemId) {
      alert("⚠️ لا يوجد معرف عنصر سلة لحذفه.");
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/cart-items/${cartItemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCartItemId(null);
      alert("🗑️ تم حذف القطعة من السلة بنجاح!");
    } catch (err) {
      console.error(err);
      alert("⚠️ لم نستطع حذف القطعة من السلة.");
    }
  };

  // ------------- Guest modal (no login) -------------
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestPhone, setGuestPhone] = useState(localStorage.getItem("guestPhone") || "");
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestError, setGuestError] = useState("");

  const openGuestModal = (e) => {
    e?.stopPropagation?.();
    setGuestError("");
    setShowGuestModal(true);
  };

  const closeGuestModal = () => {
    if (guestLoading) return;
    setShowGuestModal(false);
  };

  const confirmGuestAdd = async () => {
    if (!guestPhone || guestPhone.replace(/\D/g, "").length < 8) {
      setGuestError("رجاءً أدخل رقم جوال صحيح.");
      return;
    }

    try {
      setGuestLoading(true);
      setGuestError("");
      localStorage.setItem("guestPhone", guestPhone);

      const payload = {
        items: [{ part_id: item.id, price: item.price ?? 0 }],
        phone_number: guestPhone,
      };

      const res = await axios.post(`${API_BASE_URL}/guest-carts`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const guessed =
        res.data?.cart?.items?.find((ci) => Number(ci.part_id) === Number(item.id)) ??
        res.data?.items?.find?.((ci) => Number(ci.part_id) === Number(item.id)) ??
        null;

      if (guessed?.id) {
        setCartItemId(guessed.id);
      }
      setShowGuestModal(false);
      alert("✅ تمت إضافة القطعة إلى سلة الضيف!");
    } catch (err) {
      console.error("Guest add failed:", err);
      setGuestError("لم نتمكن من إنشاء السلة. حاول مرة ثانية.");
    } finally {
      setGuestLoading(false);
    }
  };

  // Public add handler (kept behavior)
  const handleAdd = (e) => {
    e?.stopPropagation?.();;
    const token = localStorage.getItem("token");
    if (!token) {
      openGuestModal(e);
      return;
    }
    handleAddAuth();
  };

  return {
    // type
    isPart, isCategory, isBrand, isModel,

    // basics
    id, label, price, market_price, imageSrc,

    // hover
    hovered, setHovered,

    // share
    shareRef, showShareBox, setShowShareBox, partUrl,

    // wishlist
    inWishlist, wishLoading, handleToggleWishlist, onWishlistClick,

    // cart
    cartItemId, checkingCart, handleAddAuth, handleRemove, handleAdd,

    // guest
    showGuestModal, guestPhone, setGuestPhone, guestLoading, guestError,
    openGuestModal, closeGuestModal, confirmGuestAdd,
  };
}
